/**
 * Shared saved-prompts state.
 *
 * Three places consume this:
 *   1. PPTGeneratorPage — two textareas show the picker.
 *   2. RegenerateSlidesModal — per-slide refinement textarea shows it.
 *   3. PersonalizePage — full table view + manual create/edit/delete.
 *
 * All three should see the same list and pick up create/edit/delete
 * changes from the others without an extra fetch. Rather than a Context
 * (overkill for ~3 callers) or a full state library, we use a tiny
 * module-level pub/sub: subscribers re-render when `refresh()` is
 * called from any consumer.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

let cachedPrompts = null;       // null = not yet loaded; [] = loaded but empty
let inFlight = null;            // promise dedupe — multiple concurrent
                                 // mounts share one network round-trip
const subscribers = new Set();   // setState fns from each hook user

const notify = () => {
  subscribers.forEach((cb) => cb(cachedPrompts || []));
};

async function fetchPrompts() {
  if (inFlight) return inFlight;
  inFlight = axios
    .get(`${API}/me/prompts`, { withCredentials: true })
    .then((r) => {
      cachedPrompts = r.data?.prompts || [];
      notify();
      return cachedPrompts;
    })
    .catch(() => {
      // Silent fail: the dropdown just stays empty, callers handle their
      // own errors on CRUD ops. Avoids a noisy toast on cold-start.
      cachedPrompts = cachedPrompts || [];
      notify();
      return cachedPrompts;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * `useSavedPrompts()` returns `{prompts, loading, refresh}`.
 *
 * - `prompts`  → live list of {id, name, content, created_at, updated_at}.
 * - `loading`  → true until the first fetch completes (used for
 *                skeletons in the Personalize table).
 * - `refresh()` → re-fetch and notify all subscribers. Components call
 *                 this after create/edit/delete so siblings re-render
 *                 without their own fetches.
 */
export function useSavedPrompts() {
  const [prompts, setPrompts] = useState(cachedPrompts || []);
  const [loading, setLoading] = useState(cachedPrompts === null);

  useEffect(() => {
    subscribers.add(setPrompts);
    if (cachedPrompts === null) {
      fetchPrompts().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => { subscribers.delete(setPrompts); };
  }, []);

  const refresh = useCallback(async () => {
    // Force a fresh fetch — clear the cache so the dedupe doesn't
    // hand back a stale promise.
    inFlight = null;
    cachedPrompts = null;
    setLoading(true);
    await fetchPrompts();
    setLoading(false);
  }, []);

  return { prompts, loading, refresh };
}
